import { JOB_TITLES } from '@account/constants';
import { ManagementAccountFieldName } from '@common/types/user';
import { Alert } from '@components/display/alerts';
import { InputField } from '@components/utility/inputs';
import { Select } from '@components/utility/selects';
import { IOSSwitch } from '@components/utility/switches';
import { COUNTRIES_WITH_STATES, FALLBACK_LANGUAGES } from '@constants/common';
import { useScrollIntoError } from '@hooks/useScrollIntoError';
import { AlertTitle, Box, FormControlLabel, Stack, Typography } from '@mui/material';
import { useGetDistributorsQuery, useGetSalesManagersQuery } from '@services/accounts';
import { useGetCountriesQuery } from '@services/countries';
import { useGetStatesQuery } from '@services/states';
import { useGetLanguagesQuery } from '@services/translation';
import { useGetRolesQuery } from '@services/userRoles';
import { renderCountryOption, renderCountryValue } from '@utils/common';
import { isFieldRequiredByPermission } from '@utils/fieldPermissions';
import { FOOD_PREFERENCE_OPTIONS } from '@utils/user';
import { useFormikContext } from 'formik';
import { MemberDetailsFormValues } from '../types';

export const MemberDetails = ({ isExact }: { isExact: boolean }) => {
    const formik = useFormikContext<MemberDetailsFormValues>();
    const { values, errors, touched, handleChange, handleBlur, status } = formik;
    const isFieldDisabled = (name: ManagementAccountFieldName) =>
        !status?.edit || values.field_permissions?.[name]?.read_only;
    const { data: countries, isLoading: isCountriesDataLoading } = useGetCountriesQuery();
    const { data: states, isLoading: isStatesDataLoading } = useGetStatesQuery(
        { country: values.country },
        {
            skip: !COUNTRIES_WITH_STATES.includes(values.country),
            selectFromResult: (response) => ({
                ...response,
                data: response.data?.map((state) => ({ label: state.name, value: state.name })) ?? [],
            }),
        }
    );
    const { data: languages = FALLBACK_LANGUAGES, isLoading: isLanguagesLoading } = useGetLanguagesQuery();
    const { data: salesManagers, isLoading: areSalesManagerLoading } = useGetSalesManagersQuery(undefined, {
        selectFromResult: (response) => ({
            ...response,
            data:
                response.data?.map((manager) => ({
                    label:
                        manager.first_name && manager.last_name
                            ? `${manager.first_name} ${manager.last_name}`
                            : manager.full_name,
                    value: manager.id,
                })) ?? [],
        }),
    });
    const { data: distributors, isLoading: areDistributorsLoading } = useGetDistributorsQuery(undefined, {
        selectFromResult: (response) => ({
            ...response,
            data:
                response.data?.map((distributor) => ({
                    label: distributor.company_name,
                    value: distributor.id,
                })) ?? [],
        }),
    });
    const { data: roles, isLoading: areRolesLoading } = useGetRolesQuery(undefined, {
        selectFromResult: (response) => ({
            ...response,
            data:
                response.data?.filter((role) => values.field_permissions?.role?.allowed_values?.includes(role.value)) ??
                [],
        }),
    });
    const jobTitleOptions = JOB_TITLES();

    useScrollIntoError({ formik });

    return (
        <Stack spacing="24px" py="24px">
            <Box>
                <Typography fontFamily="museo-sans" fontSize={24} letterSpacing="-1px">
                    Member Details
                </Typography>
                <Typography pt="12px" fontSize={16} letterSpacing={0}>
                    Manage user details within Victron Professional.
                </Typography>
            </Box>
            {isExact && (
                <Alert.Info>
                    <AlertTitle>Sumatra Managed Account</AlertTitle>
                    This Exact Account is managed via Sumatra. Specific details can only be modified through the Sumatra
                    platform.
                </Alert.Info>
            )}
            <InputField
                id="company_name"
                buffered
                label="Company Name"
                name="company_name"
                placeholder="Enter company name"
                helperText="A free-text field to enter the official name of the organization or business entity."
                noDebounce
            />
            <Select
                id="sales_manager"
                name="sales_manager"
                value={values.sales_manager}
                onChange={handleChange}
                onBlur={handleBlur}
                items={salesManagers}
                loading={areSalesManagerLoading || formik.isSubmitting}
                type="default"
                size="smallest"
                label="Sales Manager"
                error={touched.sales_manager && Boolean(errors.sales_manager)}
                helperText={
                    touched.sales_manager && errors.sales_manager
                        ? errors.sales_manager
                        : 'Select the sales manager responsible for handling this account'
                }
                disabled={isFieldDisabled('sales_manager')}
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.sales_manager,
                    role: values.role,
                })}
                virtualize
                loadingType="skeleton"
            />
            <Select
                id="distributor"
                name="distributor"
                value={values.distributor}
                onChange={handleChange}
                onBlur={handleBlur}
                items={distributors}
                loading={areDistributorsLoading || formik.isSubmitting}
                loadingType="skeleton"
                type="default"
                size="smallest"
                label="Distributor"
                error={touched.distributor && Boolean(errors.distributor)}
                helperText="Select the distributor associated to this account."
                disabled={isFieldDisabled('distributor')}
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.distributor,
                    role: values.role,
                })}
                virtualize
            />
            <Select
                id="role"
                name="role"
                value={values.role}
                onChange={handleChange}
                onBlur={handleBlur}
                items={roles ?? []}
                loading={areRolesLoading || formik.isSubmitting}
                loadingType="skeleton"
                type="default"
                size="smallest"
                label="Role"
                error={touched.role && Boolean(errors.role)}
                helperText="Select the role for this member."
                disabled={isFieldDisabled('role')}
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.role,
                    role: values.role,
                })}
            />
            <Select
                id="job_title"
                name="job_title"
                value={values.job_title}
                onChange={handleChange}
                onBlur={handleBlur}
                items={jobTitleOptions ?? []}
                type="default"
                size="smallest"
                label="Job Title"
                error={touched.job_title && Boolean(errors.job_title)}
                helperText={
                    touched.job_title && Boolean(errors.job_title)
                        ? errors.job_title
                        : 'Select professional title from a predefined list of standard options.'
                }
                disabled={isFieldDisabled('job_title')}
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.job_title,
                    role: values.role,
                })}
                loading={formik.isSubmitting}
                loadingType="skeleton"
            />
            <InputField
                id="job_title_other"
                name="job_title_other"
                label="Job Title Other"
                buffered
                placeholder="Enter your job title"
                helperText="A free-text field used to manually type in a custom job title if the correct option was not available in the Job Titledropdown."
                fullWidth
                noDebounce
                disabled={values.job_title !== 'other'}
            />
            <InputField
                id="first_name"
                buffered
                label="First Name"
                name="first_name"
                placeholder="Enter first name"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['first_name'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field to enter the user's given name.`}
                noDebounce
            />
            <InputField
                id="last_name"
                buffered
                label="Last Name"
                name="last_name"
                placeholder="Enter last name"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['last_name'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field to enter the user's surname.`}
                noDebounce
            />
            <InputField
                id="mobile_phone"
                buffered
                type="tel"
                name="mobile_phone"
                label="Mobile Phone"
                placeholder="Enter mobile phone number"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['mobile_phone'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field to enter the user's mobile phone number.`}
                noDebounce
            />
            <InputField
                id="telephone"
                buffered
                type="tel"
                name="telephone"
                label="Phone"
                placeholder="Enter phone number"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['telephone'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field to enter the user's primary phone number.`}
                noDebounce
            />
            <InputField
                id="fax"
                buffered
                type="tel"
                name="fax"
                label="Fax"
                placeholder="Enter fax phone number"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['fax'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field for the user's fax contact number.`}
                noDebounce
            />
            <InputField
                id="email"
                buffered
                type="email"
                required
                label="Email"
                name="email"
                placeholder="Enter email address"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['email'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field for the primary electronic mail address used for communication and login credentials.`}
                noDebounce
            />
            <Select
                id="country"
                required
                disabled={isFieldDisabled('country')}
                items={countries ?? []}
                type="radio"
                size="smallest"
                label="Country"
                name="country"
                value={values.country}
                error={touched.country && !!errors.country}
                helperText={`${
                    touched.country && errors.country
                        ? errors.country
                        : isFieldRequiredByPermission({
                              permission: values.field_permissions?.['country'],
                              role: values.role,
                          })
                        ? 'Required'
                        : 'Optional'
                } field to specify the user's country of residence or operation.`}
                onChange={handleChange}
                onBlur={handleBlur}
                searchable
                virtualize
                renderOption={renderCountryOption}
                renderValue={(value) => renderCountryValue(value as string, countries ?? [])}
                loading={isCountriesDataLoading || formik.isSubmitting}
                loadingType="skeleton"
            />
            <Select
                id="state"
                items={states ?? []}
                type="radio"
                size="smallest"
                label="State/Province"
                name="state"
                value={values.state}
                error={touched.state && !!errors.state}
                helperText={`${
                    touched.state && errors.state
                        ? errors.state
                        : isFieldRequiredByPermission({
                              permission: values.field_permissions?.['state'],
                              role: values.role,
                          })
                        ? 'Required'
                        : 'Optional'
                } field for the regional state, territory, or province.`}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isStatesDataLoading || states?.length === 0 || isFieldDisabled('state')}
                loading={isStatesDataLoading || formik.isSubmitting}
                loadingType="skeleton"
            />
            <InputField
                id="city"
                buffered
                noDebounce
                label="City"
                name="city"
                placeholder="Enter city"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['city'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field for the city or town name.`}
            />
            <InputField
                id="address1"
                buffered
                noDebounce
                name="address1"
                placeholder="Enter full address"
                label="Address Line 1"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['address1'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field for the primary street address or physical location.`}
            />
            <InputField
                id="address2"
                buffered
                noDebounce
                name="address2"
                placeholder="Enter full address"
                label="Address Line 2"
                helperText={`${
                    isFieldRequiredByPermission({
                        permission: values.field_permissions?.['address2'],
                        role: values.role,
                    })
                        ? 'Required'
                        : 'Optional'
                } field for supplementary address details (e.g., apartment number, suite, unit, or floor).`}
            />
            <Select
                id="language"
                name="language"
                value={values.language}
                onChange={handleChange}
                onBlur={handleBlur}
                items={languages}
                type="default"
                renderValue={(value) => renderCountryValue(value as string, languages)}
                size="smallest"
                renderOption={renderCountryOption}
                label="Language"
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.['language'],
                    role: values.role,
                })}
                error={touched.language && Boolean(errors.language)}
                helperText={`${
                    touched.language && errors.language
                        ? errors.language
                        : isFieldRequiredByPermission({
                              permission: values.field_permissions?.['language'],
                              role: values.role,
                          })
                        ? 'Required'
                        : 'Optional'
                } field to select the user's preferred language for platform interactions and communications.`}
                loading={isLanguagesLoading || formik.isSubmitting}
                loadingType="skeleton"
                disabled={isFieldDisabled('language')}
            />
            <Select
                id="food_preference"
                name="food_preference"
                value={values.food_preference}
                onChange={handleChange}
                onBlur={handleBlur}
                items={FOOD_PREFERENCE_OPTIONS}
                type="default"
                size="smallest"
                label="Food Preference"
                error={touched.food_preference && Boolean(errors.food_preference)}
                helperText={`${
                    touched.food_preference && errors.food_preference
                        ? errors.food_preference
                        : isFieldRequiredByPermission({
                              permission: values.field_permissions?.['food_preference'],
                              role: values.role,
                          })
                        ? 'Required'
                        : 'Optional'
                } drop-down field to select dietary preferences or catering requirements (typically used for attending in-person events or training sessions).`}
                disabled={isFieldDisabled('food_preference')}
                loading={formik.isSubmitting}
                loadingType="skeleton"
            />
            <FormControlLabel
                value="receive_mailings_targeted_at_professionals"
                labelPlacement="start"
                control={
                    <IOSSwitch
                        id="receive_mailings_targeted_at_professionals"
                        name="receive_mailings_targeted_at_professionals"
                        checked={Boolean(values.receive_mailings_targeted_at_professionals)}
                        onChange={(_e, checked) =>
                            handleChange({
                                target: {
                                    name: 'receive_mailings_targeted_at_professionals',
                                    value: checked ? 1 : 0,
                                },
                            })
                        }
                        onBlur={handleBlur}
                        disabled={isFieldDisabled('receive_mailings_targeted_at_professionals')}
                    />
                }
                label="Receive mailings targeted at professionals?"
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.['receive_mailings_targeted_at_professionals'],
                    role: values.role,
                })}
                classes={{
                    root: 'justify-between',
                }}
            />
            <hr />
            <FormControlLabel
                value="is_eligible_for_extended_warranty"
                labelPlacement="start"
                control={
                    <IOSSwitch
                        id="is_eligible_for_extended_warranty"
                        name="is_eligible_for_extended_warranty"
                        checked={values.is_eligible_for_extended_warranty}
                        onChange={(_e, checked) =>
                            handleChange({
                                target: {
                                    name: 'is_eligible_for_extended_warranty',
                                    value: checked,
                                },
                            })
                        }
                        onBlur={handleBlur}
                        disabled={isFieldDisabled('is_eligible_for_extended_warranty')}
                    />
                }
                label="Is eligible for extended warranty?"
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.['is_eligible_for_extended_warranty'],
                    role: values.role,
                })}
                classes={{
                    root: 'justify-between',
                }}
            />
            <hr />
            <FormControlLabel
                value="is_news_subscriber"
                labelPlacement="start"
                control={
                    <IOSSwitch
                        id="is_news_subscriber"
                        name="is_news_subscriber"
                        checked={values.is_news_subscriber}
                        onChange={(_e, checked) =>
                            handleChange({
                                target: {
                                    name: 'is_news_subscriber',
                                    value: checked,
                                },
                            })
                        }
                        onBlur={handleBlur}
                        disabled={isFieldDisabled('is_news_subscriber')}
                    />
                }
                label="Is news subscriber?"
                required={isFieldRequiredByPermission({
                    permission: values.field_permissions?.['is_news_subscriber'],
                    role: values.role,
                })}
                classes={{
                    root: 'justify-between',
                }}
            />
            <hr />
        </Stack>
    );
};
